import React, {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  MenuItem,
  Autocomplete,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import axiosInstance from "../../utils/axiosInstance";
import {BASE_URL, USER_BY_ROLE, ROLES} from "../../utils/constants";
import {createHonorarium} from "../../store/thunks/honorariumThunks";
import {showError, showSuccess} from "../../utils/notification";

const MONTHS = [
  {value: 1, label: "January"},
  {value: 2, label: "February"},
  {value: 3, label: "March"},
  {value: 4, label: "April"},
  {value: 5, label: "May"},
  {value: 6, label: "June"},
  {value: 7, label: "July"},
  {value: 8, label: "August"},
  {value: 9, label: "September"},
  {value: 10, label: "October"},
  {value: 11, label: "November"},
  {value: 12, label: "December"},
];

const numField = (val) => (val === "" || val === null ? 0 : Number(val));

// Dialog for manually creating a honorarium record. Used by Accountant / MD /
// Super Admin while the auto-calc cron is paused (OFFICE_ONLY_MODE) or to
// backfill missed months. Mirrors POST /api/honorarium.
const AddHonorariumDialog = ({open, onClose, onCreated, defaultMonth, defaultYear}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [teacherOptions, setTeacherOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [target, setTarget] = useState(null);

  const [form, setForm] = useState({
    month: defaultMonth || new Date().getMonth() + 1,
    year: defaultYear || new Date().getFullYear(),
    totalPresentDays: "",
    totalAbsentDays: "",
    deduction: 0,
    daswand: 0,
    travelling_allowance: 0,
    m_travelling_allowance: 0,
    totalHonorarium: "",
    netHonorarium: "",
    remarks: "",
  });

  // Fetch teachers as the primary pickable population. Other roles can be
  // added later if needed.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open) return;
      setOptionsLoading(true);
      try {
        const res = await axiosInstance.get(
          `${BASE_URL}${USER_BY_ROLE}?role=${ROLES.TEACHER}&page=1&limit=200&email=`
        );
        if (!cancelled) {
          setTeacherOptions(res?.data?.data?.users || []);
        }
      } catch (e) {
        if (!cancelled) setTeacherOptions([]);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Reset form on close so the next open is clean.
  useEffect(() => {
    if (!open) {
      setForm({
        month: defaultMonth || new Date().getMonth() + 1,
        year: defaultYear || new Date().getFullYear(),
        totalPresentDays: "",
        totalAbsentDays: "",
        deduction: 0,
        daswand: 0,
        travelling_allowance: 0,
        m_travelling_allowance: 0,
        totalHonorarium: "",
        netHonorarium: "",
        remarks: "",
      });
      setTarget(null);
      setErrorMsg(null);
    }
  }, [open, defaultMonth, defaultYear]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({...prev, [field]: e.target.value}));
  };

  const validate = () => {
    if (!target?._id) return "Pick a user to create the honorarium for";
    if (!form.month || !form.year) return "Month and year are required";
    if (form.totalPresentDays === "" || form.totalAbsentDays === "")
      return "Present and absent day counts are required";
    if (form.totalHonorarium === "" || form.netHonorarium === "")
      return "Total and net honorarium are required";
    if (Number(form.netHonorarium) < 0) return "Net honorarium cannot be negative";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setErrorMsg(err);
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const payload = {
        userId: target._id,
        month: Number(form.month),
        year: Number(form.year),
        totalPresentDays: numField(form.totalPresentDays),
        totalAbsentDays: numField(form.totalAbsentDays),
        deduction: numField(form.deduction),
        daswand: numField(form.daswand),
        travelling_allowance: numField(form.travelling_allowance),
        m_travelling_allowance: numField(form.m_travelling_allowance),
        totalHonorarium: numField(form.totalHonorarium),
        netHonorarium: numField(form.netHonorarium),
        ...(form.remarks ? {remarks: form.remarks} : {}),
      };
      const action = await dispatch(createHonorarium(payload));
      if (action?.error || action?.payload?.status === "error") {
        const msg =
          action?.payload?.message ||
          action?.error?.message ||
          (typeof action?.payload === "string" ? action.payload : null) ||
          "Failed to create honorarium";
        setErrorMsg(msg);
        showError(msg);
      } else {
        showSuccess("Honorarium created");
        if (onCreated) onCreated();
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({length: 5}, (_, i) => currentYear - 2 + i);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Add Honorarium (Manual Entry)</DialogTitle>
      <DialogContent dividers>
        {errorMsg && (
          <Alert severity="error" sx={{mb: 2}}>
            {errorMsg}
          </Alert>
        )}
        <Box sx={{mb: 2}}>
          <Autocomplete
            options={teacherOptions}
            loading={optionsLoading}
            value={target}
            onChange={(_, v) => setTarget(v)}
            getOptionLabel={(o) =>
              o ? `${o.firstName || ""} ${o.lastName || ""} (${o.email})`.trim() : ""
            }
            isOptionEqualToValue={(a, b) => a?._id === b?._id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Teacher"
                required
                helperText="Pick the teacher (or other active user) this honorarium is for"
              />
            )}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              label="Month"
              value={form.month}
              onChange={handleChange("month")}>
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              label="Year"
              value={form.year}
              onChange={handleChange("year")}>
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Present Days"
              value={form.totalPresentDays}
              onChange={handleChange("totalPresentDays")}
              inputProps={{min: 0}}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Absent Days"
              value={form.totalAbsentDays}
              onChange={handleChange("totalAbsentDays")}
              inputProps={{min: 0}}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Deduction (₹)"
              value={form.deduction}
              onChange={handleChange("deduction")}
              inputProps={{min: 0}}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Daswand (₹)"
              value={form.daswand}
              onChange={handleChange("daswand")}
              inputProps={{min: 0}}
              helperText="5% of total honorarium"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Travelling Allowance (₹)"
              value={form.travelling_allowance}
              onChange={handleChange("travelling_allowance")}
              inputProps={{min: 0}}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Meeting T.A. (₹)"
              value={form.m_travelling_allowance}
              onChange={handleChange("m_travelling_allowance")}
              inputProps={{min: 0}}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Total Honorarium (₹)"
              value={form.totalHonorarium}
              onChange={handleChange("totalHonorarium")}
              inputProps={{min: 0}}
              required
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Net Honorarium (₹)"
              value={form.netHonorarium}
              onChange={handleChange("netHonorarium")}
              inputProps={{min: 0}}
              required
              helperText="After deductions"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Remarks (optional)"
              value={form.remarks}
              onChange={handleChange("remarks")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}>
          {submitting ? "Submitting..." : "Create Honorarium"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddHonorariumDialog;
